/**
 * UI Slice
 * Manages UI state such as sidebar, modals, notifications, etc.
 */

import { createSlice } from '@reduxjs/toolkit';

// Initial state
const initialState = {
  // Sidebar state
  sidebar: {
    isCollapsed: false,
    isMobileOpen: false,
  },
  
  // Modal state
  modals: {
    isUserModalOpen: false,
    isInventoryModalOpen: false,
    isBudgetModalOpen: false,
    isConfirmationModalOpen: false,
    modalData: null,
  },
  
  // Notifications/Toasts
  notifications: [],
  
  // Loading states
  globalLoading: false,
  
  // Breadcrumbs
  breadcrumbs: [],
  
  // Page title
  pageTitle: 'Dashboard',
};

// Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Sidebar actions
    toggleSidebar: (state) => {
      state.sidebar.isCollapsed = !state.sidebar.isCollapsed;
    },
    setSidebarCollapsed: (state, action) => {
      state.sidebar.isCollapsed = action.payload;
    },
    toggleMobileSidebar: (state) => {
      state.sidebar.isMobileOpen = !state.sidebar.isMobileOpen;
    },
    setMobileSidebarOpen: (state, action) => {
      state.sidebar.isMobileOpen = action.payload;
    },
    
    // Modal actions
    openModal: (state, action) => {
      const { modalName, data } = action.payload;
      state.modals[modalName] = true;
      if (data) {
        state.modals.modalData = data;
      }
    },
    closeModal: (state, action) => {
      const modalName = action.payload;
      state.modals[modalName] = false;
      state.modals.modalData = null;
    },
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach((key) => {
        if (key !== 'modalData') {
          state.modals[key] = false;
        }
      });
      state.modals.modalData = null;
    },
    
    // Notification actions
    addNotification: (state, action) => {
      const notification = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    
    // Global loading
    setGlobalLoading: (state, action) => {
      state.globalLoading = action.payload;
    },
    
    // Breadcrumbs
    setBreadcrumbs: (state, action) => {
      state.breadcrumbs = action.payload;
    },
    addBreadcrumb: (state, action) => {
      state.breadcrumbs.push(action.payload);
    },
    clearBreadcrumbs: (state) => {
      state.breadcrumbs = [];
    },
    
    // Page title
    setPageTitle: (state, action) => {
      state.pageTitle = action.payload;
    },
  },
});

// Actions
export const {
  toggleSidebar,
  setSidebarCollapsed,
  toggleMobileSidebar,
  setMobileSidebarOpen,
  openModal,
  closeModal,
  closeAllModals,
  addNotification,
  removeNotification,
  clearNotifications,
  setGlobalLoading,
  setBreadcrumbs,
  addBreadcrumb,
  clearBreadcrumbs,
  setPageTitle,
} = uiSlice.actions;

// Selectors
export const selectSidebar = (state) => state.ui.sidebar;
export const selectSidebarCollapsed = (state) => state.ui.sidebar.isCollapsed;
export const selectMobileSidebarOpen = (state) => state.ui.sidebar.isMobileOpen;
export const selectModals = (state) => state.ui.modals;
export const selectModalData = (state) => state.ui.modals.modalData;
export const selectNotifications = (state) => state.ui.notifications;
export const selectGlobalLoading = (state) => state.ui.globalLoading;
export const selectBreadcrumbs = (state) => state.ui.breadcrumbs;
export const selectPageTitle = (state) => state.ui.pageTitle;

export default uiSlice.reducer;
